import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchMusicLibrary } from "@/lib/storyMusic";
import { FileUpload, resolveMediaUrl, uploadMediaFile } from "@/components/FileUpload";
import { Waveform } from "@/components/Waveform";
import { Music, Play, Pause, X, Loader2, Flame, Scissors, Star, Search, Upload, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const fmt = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
};

// Reusable "add a sound" picker (built-in library + MP3 import), Story-style.
// `music` = { url, title, start } | null. `onChange(music | null)`.
// Extras: a "Tendances" row (most-used sounds) and a start-point selector so
// the author can pick which part of the track plays over their photo/video.
export const MusicPicker = ({ music, onChange, disabled, disabledReason, previewVolume = 1, onPreview, t = (k, d) => d, testId = "post-music" }) => {
  const [lib, setLib] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dur, setDur] = useState(0);
  const [query, setQuery] = useState("");
  const previewRef = useRef(null);
  const metaRef = useRef(null);

  // Localise built-in track names (an English member shouldn't see "Louange").
  // Custom / admin-uploaded tracks have no i18n key → keep their literal title.
  const trackTitle = (m) => (m?.i18nKey ? t(m.i18nKey, m.title) : m?.title) || "";

  useEffect(() => {
    let alive = true;
    fetchMusicLibrary().then((x) => { if (alive) setLib(x); });
    return () => { alive = false; try { previewRef.current?.pause(); } catch { /* noop */ } };
  }, []);

  // Load the selected track's duration to bound the start-point slider.
  useEffect(() => {
    setDur(0);
    const a = metaRef.current;
    if (!a || !music?.url) return;
    a.src = resolveMediaUrl(music.url);
    const onMeta = () => {
      const d = isFinite(a.duration) ? a.duration : 0;
      setDur(d);
      const max = Math.max(0, Math.floor(d - 3));
      if (d && (music.start || 0) > max) onChange({ ...music, start: max });
    };
    a.addEventListener("loadedmetadata", onMeta);
    return () => a.removeEventListener("loadedmetadata", onMeta);
  }, [music?.url]);

  // Keep the picker compact: show only the 7 most-used sounds by default; the full
  // library stays reachable through the search field.
  const popular = useMemo(
    () => [...lib].sort((a, b) => (b.uses || 0) - (a.uses || 0)).slice(0, 7),
    [lib],
  );
  const featured = useMemo(() => lib.find((m) => m.featured), [lib]);
  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () => (q ? lib.filter((m) => `${trackTitle(m)} ${m.title || ""}`.toLowerCase().includes(q)) : lib),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lib, q, t],
  );

  const togglePreview = (track, from = 0) => {
    const a = previewRef.current; if (!a) return;
    if (preview === track.key) { a.pause(); setPreview(null); onPreview?.(false); return; }
    a.src = resolveMediaUrl(track.url);
    a.currentTime = from || 0;
    a.volume = Math.max(0, Math.min(1, previewVolume));
    a.play().catch(() => {});
    setPreview(track.key);
    onPreview?.(true);
  };

  const select = (m) => {
    const active = music?.url === m.url;
    onChange(active ? null : { url: m.url, title: trackTitle(m), start: 0 });
  };

  const setStart = (v) => {
    if (!music) return;
    const max = Math.max(0, Math.floor((dur || 30) - 3));
    const clamped = Math.min(Math.max(0, v), max);
    onChange({ ...music, start: clamped });
    const a = previewRef.current;
    if (a && preview) { try { a.currentTime = clamped; } catch { /* noop */ } }
  };

  const handleFile = async (files) => {
    const f = files && files[0]; if (!f) return;
    setBusy(true);
    try {
      const data = await uploadMediaFile(f, { context: "feed" });
      onChange({ url: data.url, title: f.name.replace(/\.[^.]+$/, "").slice(0, 60), start: 0 });
      toast.success(t("composer.music_added", "Son ajouté ♪"));
    } catch (e) { toast.error(e?.friendlyMessage || t("composer.music_error", "Échec de l'import du son")); }
    finally { setBusy(false); }
  };

  const Chip = ({ m, showFlame }) => {
    const active = music?.url === m.url;
    return (
      <div
        className="shrink-0 flex items-center gap-1 rounded-full pl-1 pr-2.5 py-1 text-xs whitespace-nowrap border"
        style={{ borderColor: active ? "var(--yalola-primary)" : "var(--yalola-border)", color: active ? "var(--yalola-primary)" : "inherit", fontWeight: active ? 600 : 400 }}
        data-testid={`${testId}-track-${m.key}`}
      >
        <button type="button" onClick={() => togglePreview(m, active ? (music?.start || 0) : 0)} className="h-5 w-5 grid place-items-center rounded-full" data-testid={`${testId}-preview-${m.key}`}>
          {preview === m.key ? <Pause size={12} /> : <Play size={12} />}
        </button>
        <button type="button" onClick={() => select(m)} data-testid={`${testId}-select-${m.key}`} className="flex items-center gap-1">
          {m.featured && <Star size={11} className="text-amber-400 fill-amber-400" />}
          {showFlame && <Flame size={11} className="text-orange-500" />}
          {trackTitle(m)}
        </button>
      </div>
    );
  };

  if (disabled) {
    return (
      <div className="text-[11px] px-1 py-1" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testId}-disabled`}>
        {disabledReason}
      </div>
    );
  }

  const maxStart = Math.max(0, Math.floor((dur || 30) - 3));

  return (
    <div className="rounded-lg border p-2.5" style={{ borderColor: "var(--yalola-border)" }} data-testid={`${testId}-picker`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs font-semibold flex items-center gap-1.5"><Music size={13} /> {t("composer.music", "Choisir un fond musical")}</div>
        <FileUpload accept="audio/*" onFiles={handleFile} variant="custom" testId={`${testId}-upload`} context="feed">
          <span className="shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-white transition-transform active:scale-95" style={{ background: "var(--yalola-primary)" }}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {busy ? t("composer.music_uploading", "Import…") : t("composer.music_add_own", "Ajouter mon son")}
          </span>
        </FileUpload>
      </div>

      {/* Search — essential once the library grows to dozens of tracks */}
      <div className="relative mb-2">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--yalola-text-muted)" }} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("composer.music_search", "Rechercher un son")}
          className="w-full rounded-full border pl-8 pr-8 py-1.5 text-xs bg-transparent outline-none focus:border-[color:var(--yalola-primary)]"
          style={{ borderColor: "var(--yalola-border)" }}
          data-testid={`${testId}-search`}
          aria-label={t("composer.music_search", "Rechercher un son")}
        />
        {query && (
          <button type="button" onClick={() => setQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2" data-testid={`${testId}-search-clear`} aria-label="Effacer">
            <X size={13} style={{ color: "var(--yalola-text-muted)" }} />
          </button>
        )}
      </div>

      {!q && featured && (
        <div className="mb-2" data-testid={`${testId}-featured`}>
          <div className="text-[10px] uppercase tracking-wide font-bold mb-1 flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}>
            <Star size={11} className="text-amber-400 fill-amber-400" /> {t("composer.music_featured", "À la une")}
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto hide-scrollbar py-0.5">
            <Chip m={featured} />
          </div>
        </div>
      )}

      {q ? (
        filtered.length === 0 ? (
          <div className="text-[11px] py-2 text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testId}-no-results`}>
            {t("composer.music_no_results", "Aucun son trouvé")}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5 py-0.5 max-h-[8.5rem] overflow-y-auto hide-scrollbar" data-testid={`${testId}-lib`}>
            {filtered.map((m) => <Chip key={m.key} m={m} />)}
          </div>
        )
      ) : (
        <>
          <div className="text-[10px] uppercase tracking-wide font-bold mb-1" style={{ color: "var(--yalola-text-muted)" }}>
            {t("composer.music_suggested", "Sons suggérés")}
          </div>
          <div className="flex flex-wrap gap-1.5 py-0.5" data-testid={`${testId}-lib`}>
            {popular.map((m) => <Chip key={m.key} m={m} />)}
          </div>
          {lib.length > popular.length && (
            <p className="text-[10.5px] mt-1.5 flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testId}-more-hint`}>
              <Search size={11} /> {t("composer.music_more_hint", `Recherche pour voir les ${lib.length} sons`)}
            </p>
          )}
        </>
      )}

      {music && (
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-2 text-xs" data-testid={`${testId}-selected`}>
            <span className="font-semibold truncate" style={{ color: "var(--yalola-primary)" }}>♪ {music.title}</span>
            <button type="button" onClick={() => onChange(null)} className="text-red-500 ml-auto" data-testid={`${testId}-remove`}><X size={13} /></button>
          </div>
          {/* Start-point (pick a part of the audio) */}
          <div data-testid={`${testId}-trim`}>
            <div className="flex items-center justify-between text-[11px] mb-1" style={{ color: "var(--yalola-text-muted)" }}>
              <span className="flex items-center gap-1"><Scissors size={11} /> {t("composer.music_start", "Départ")}</span>
              <span className="tabular-nums font-semibold" style={{ color: "var(--yalola-primary)" }}>{fmt(music.start || 0)}{dur ? ` / ${fmt(dur)}` : ""}</span>
            </div>
            <input
              type="range" min={0} max={maxStart} step={0.5}
              value={Math.min(music.start || 0, maxStart)}
              onChange={(e) => setStart(parseFloat(e.target.value))}
              className="w-full accent-[color:var(--yalola-primary)]"
              data-testid={`${testId}-start-slider`}
              aria-label={t("composer.music_start", "Départ")}
            />
            <Waveform url={music.url} start={music.start || 0} duration={dur} onSeek={setStart} className="mt-1" testId={`${testId}-waveform`} />
            <div className="mt-1 flex items-center justify-between gap-2">
              <button type="button" onClick={() => togglePreview({ key: "__sel", url: music.url }, music.start || 0)} className="text-[11px] font-semibold flex items-center gap-1" style={{ color: "var(--yalola-primary)" }} data-testid={`${testId}-preview-from`}>
                {preview === "__sel" ? <Pause size={12} /> : <Play size={12} />} {t("composer.music_preview_from", "Aperçu depuis ce point")}
              </button>
              {(music.start || 0) > 0 && (
                <button type="button" onClick={() => setStart(0)} className="text-[11px] font-semibold flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testId}-restart`}>
                  <RotateCcw size={12} /> {t("composer.music_restart", "Recommencer au début")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <audio ref={previewRef} onEnded={() => { setPreview(null); onPreview?.(false); }} className="hidden" />
      <audio ref={metaRef} preload="metadata" className="hidden" />
    </div>
  );
};

export default MusicPicker;
